import { customAuth } from './customAuth';
import { EncryptionService } from './encryptionService';
import { AuthenticationError, extractAuthError } from '../utils/authError';
import { t } from '../i18n/translate';

import { getApiBaseUrl } from '../config/constants';
import { perfCount, perfMark, perfTimeAsync } from '../utils/perfTimer';

// Same AWS backend for localhost (static serve) and production; /api proxy is dev-server only.
const API_BASE_URL = getApiBaseUrl();

export interface ProgressItem {
  PK: string;
  SK: string;
  program: string;
  track: string;
  step_number?: number;
  step_title?: string;
  completed?: boolean;
  started_at?: string;
  completed_at?: string;
  data?: any;
  created_at?: string;
  updated_at?: string;
}

export interface StepProgressRequest {
  program: string;
  track: string;
  step_number: number;
  step_title: string;
  completed: boolean;
  data?: any;
  started_at?: string;
  completed_at?: string;
}

export interface JourneyProgress {
  opted_out: boolean;
  journey_start?: string;
  all_steps_completed?: boolean;
  any_steps_started?: boolean;
}

export interface UserPreferences {
  date_display_opted_out: boolean;
}

/** Share one in-flight GET /progress/{program} per program; cleared when it settles (not a stale cache). */
const programProgressInFlight = new Map<string, Promise<ProgressItem[]>>();

class ProgressService {
  private async getHeaders(): Promise<HeadersInit> {
    const token = customAuth.getToken();
    if (!token) {
      throw new AuthenticationError(t('auth.error.no_token'));
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Check if a response is a 401 authentication error and throw AuthenticationError if so
   */
  private async checkAuthError(response: Response): Promise<void> {
    if (response.status === 401) {
      const errorText = await response.text();
      throw new AuthenticationError(extractAuthError(response, errorText));
    }
  }

  async getUserProgress(): Promise<ProgressItem[]> {
    const response = await fetch(`${API_BASE_URL}/progress`, {
      method: 'GET',
      headers: await this.getHeaders()
    });
    await this.checkAuthError(response);
    if (!response.ok) {
      throw new Error(`Failed to get user progress: ${response.statusText}`);
    }
    const data = await response.json();
    return data.progress || [];
  }

  private async fetchProgramProgressFromNetwork(program: string): Promise<ProgressItem[]> {
    perfCount(`GET /progress/${program}`);
    return perfTimeAsync(`GET /progress/${program}`, async () => {
      const response = await fetch(`${API_BASE_URL}/progress/${encodeURIComponent(program)}`, {
        method: 'GET',
        headers: await this.getHeaders(),
      });
      await this.checkAuthError(response);
      if (!response.ok) {
        throw new Error(`Failed to get program progress: ${response.statusText}`);
      }
      const data = await response.json();
      return data.progress || [];
    });
  }

  async getProgramProgress(program: string): Promise<ProgressItem[]> {
    const normalized = program.trim();
    const inFlight = programProgressInFlight.get(normalized);
    if (inFlight) {
      perfMark(`GET /progress/${normalized}`, 'joined in-flight request');
      return inFlight;
    }

    const promise = this.fetchProgramProgressFromNetwork(normalized).finally(() => {
      programProgressInFlight.delete(normalized);
    });
    programProgressInFlight.set(normalized, promise);
    return promise;
  }

  async createProgram(request: { program: string; program_description?: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/progress/program`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(request)
    });
    await this.checkAuthError(response);
    if (!response.ok) {
      throw new Error(`Failed to create program: ${response.statusText}`);
    }
    return response.json();
  }

  async createTrack(request: { program: string; track: string; track_description?: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/progress/track`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(request)
    });
    await this.checkAuthError(response);
    if (!response.ok) {
      throw new Error(`Failed to create track: ${response.statusText}`);
    }
    return response.json();
  }

  async initializeProgress(): Promise<void> {
    return perfTimeAsync('initializeProgress (total)', async () => {
    try {
      await perfTimeAsync('POST /progress/program', () =>
        this.createProgram({
          program: 'SAA',
          program_description: 'Sex Addicts Anonymous',
        })
      );
    } catch (error) {
      console.log('SAA program might already exist:', error);
    }
    try {
      await perfTimeAsync('POST /progress/track', () =>
        this.createTrack({
          program: 'SAA',
          track: '12-steps',
          track_description: 'The 12 Steps of SAA',
        })
      );
    } catch (error) {
      console.log('12-steps track might already exist:', error);
    }
    });
  }

  /** Create program/track records only when the account has no progress data yet. */
  async ensureProgressInitialized(program: string = 'SAA'): Promise<void> {
    const items = await this.getProgramProgress(program);
    if (items.length > 0) return;
    await this.initializeProgress();
  }

  async getStepProgress(program: string, track: string, stepNumber: number): Promise<ProgressItem | null> {
    return perfTimeAsync(`getStepProgress(step ${stepNumber})`, async () => {
    try {
      const progress = await this.getProgramProgress(program);
      const stepKey = `${program}#${track}#step${stepNumber}`;
      const stepRecord = progress.find(item => item.SK === stepKey);
      
      if (!stepRecord) {
        return null;
      }
      
      // Handle decryption if data is encrypted
      if (stepRecord.data?.encryptedData) {
        try {
          const decryptedData = await EncryptionService.decrypt(stepRecord.data.encryptedData);
          
          // Merge decrypted data back into the step record
          return {
            ...stepRecord,
            data: {
              ...stepRecord.data,
              ...decryptedData,
              // Remove the encrypted data from the response
              encryptedData: undefined,
              encrypted: undefined
            }
          };
        } catch (decryptionError) {
          // Silently handle decryption errors (likely old data with different encryption key)
          // Return data without decryption if it fails
          return stepRecord;
        }
      }
      
      return stepRecord;
    } catch (error) {
      console.error('Error getting step progress:', error);
      return null;
    }
    });
  }

  private findStepRecord(
    items: ProgressItem[],
    program: string,
    track: string,
    stepNumber: number
  ): ProgressItem | undefined {
    const stepKey = `${program}#${track}#step${stepNumber}`;
    return items.find((item) => item.SK === stepKey);
  }

  private async decryptRecordData(record: ProgressItem): Promise<Record<string, unknown>> {
    if (!record.data?.encryptedData) return {};
    try {
      return await EncryptionService.decrypt(record.data.encryptedData);
    } catch {
      return {};
    }
  }

  private buildStepOverviewItem(record: ProgressItem, decryptedData: Record<string, unknown>): ProgressItem {
    const finalData: Record<string, unknown> = {
      ...record.data,
      ...decryptedData,
      encryptedData: record.data?.encryptedData,
      encrypted: record.data?.encrypted,
    };
    const nested = finalData.data as { actions?: unknown[] } | undefined;
    if (nested?.actions && !finalData.actions) {
      finalData.actions = nested.actions;
    }
    return { ...record, data: finalData };
  }

  /** Full decrypt for step pages (strips encrypted payload from response). */
  private async resolveStepRecordFull(record: ProgressItem): Promise<ProgressItem> {
    if (!record.data?.encryptedData) {
      return record;
    }
    try {
      const decryptedData = await this.decryptRecordData(record);
      return {
        ...record,
        data: {
          ...record.data,
          ...decryptedData,
          encryptedData: undefined,
          encrypted: undefined,
        },
      };
    } catch {
      return record;
    }
  }

  /**
   * Single fetch for step page(s): init only when empty, optional migrate from items,
   * decrypt requested steps in parallel (no per-step GET loop).
   */
  async loadStepPagesProgress(
    program: string,
    track: string,
    stepNumbers: number[],
    options?: { migrateGettingStarted?: boolean; includeJourney?: boolean }
  ): Promise<{
    steps: Record<number, ProgressItem | null>;
    journeyProgress: JourneyProgress | null;
  }> {
    const runMigrate =
      options?.migrateGettingStarted ?? stepNumbers.some((n) => n === 0);
    const label =
      stepNumbers.length === 1
        ? `loadStepPageProgress(step ${stepNumbers[0]})`
        : `loadStepPagesProgress(steps ${stepNumbers.join(',')})`;

    return perfTimeAsync(label, async () => {
      const journeyPromise = options?.includeJourney
        ? this.getJourneyProgress().catch((err) => {
            console.error('Error getting journey progress:', err);
            return null;
          })
        : Promise.resolve(null);

      let items = await this.getProgramProgress(program);
      if (items.length === 0) {
        await this.initializeProgress();
        items = await this.getProgramProgress(program);
      }

      if (runMigrate && (await this.migrateGettingStartedFromItems(items, program, track))) {
        items = await this.getProgramProgress(program);
      }

      const stepEntries = await Promise.all(
        stepNumbers.map(async (stepNumber) => {
          const record = this.findStepRecord(items, program, track, stepNumber);
          if (!record) return [stepNumber, null] as const;
          return [stepNumber, await this.resolveStepRecordFull(record)] as const;
        })
      );

      const journeyProgress = await journeyPromise;
      return { steps: Object.fromEntries(stepEntries), journeyProgress };
    });
  }

  /**
   * Single fetch for a step page: init only when empty, optional migrate from items,
   * decrypt one step record (no per-step GET loop).
   */
  async loadStepPageProgress(
    program: string,
    track: string,
    stepNumber: number,
    options?: { migrateGettingStarted?: boolean }
  ): Promise<ProgressItem | null> {
    const runMigrate = options?.migrateGettingStarted ?? stepNumber === 0;
    const { steps } = await this.loadStepPagesProgress(program, track, [stepNumber], {
      migrateGettingStarted: runMigrate,
    });
    return steps[stepNumber] ?? null;
  }

  async buildAllStepProgressFromItems(
    program: string,
    track: string,
    items: ProgressItem[]
  ): Promise<Record<number, ProgressItem | null>> {
    const stepRecords = items.filter((item) => item.SK.startsWith(`${program}#${track}#step`));
    const entries = await Promise.all(
      stepRecords
        .filter((record): record is ProgressItem & { step_number: number } => typeof record.step_number === 'number')
        .map(async (record) => {
          const decryptedData = await this.decryptRecordData(record);
          return [record.step_number, this.buildStepOverviewItem(record, decryptedData)] as const;
        })
    );
    return Object.fromEntries(entries);
  }

  async getAllStepProgress(program: string, track: string): Promise<Record<number, ProgressItem | null>> {
    return perfTimeAsync('getAllStepProgress (total)', async () => {
      const progress = await this.getProgramProgress(program);
      return this.buildAllStepProgressFromItems(program, track, progress);
    });
  }

  /**
   * Single fetch for the SAA steps tab: one GET /progress/{program}, journey in parallel,
   * migrate from preloaded items (no extra step GETs).
   */
  async loadStepsOverviewProgress(
    program: string = 'SAA',
    track: string = '12-steps'
  ): Promise<{
    stepProgress: Record<number, ProgressItem | null>;
    journeyProgress: JourneyProgress | null;
  }> {
    return perfTimeAsync('loadStepsOverviewProgress (total)', async () => {
      const journeyPromise = this.getJourneyProgress().catch((err) => {
        console.error('Error getting journey progress:', err);
        return null;
      });

      let items = await this.getProgramProgress(program);
      if (items.length === 0) {
        await this.initializeProgress();
        items = await this.getProgramProgress(program);
      }

      if (await this.migrateGettingStartedFromItems(items, program, track)) {
        items = await this.getProgramProgress(program);
      }

      const stepProgress = await this.buildAllStepProgressFromItems(program, track, items);
      const journeyProgress = await journeyPromise;

      return { stepProgress, journeyProgress };
    });
  }

  async saveStepActions(program: string, track: string, stepNumber: number, actions: any[]): Promise<any> {
    return this.updateStepProgress({
      program,
      track,
      step_number: stepNumber,
      step_title: `Step ${stepNumber}`,
      completed: false,
      data: { actions }
    });
  }

  async updateStepProgress(request: StepProgressRequest): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      // Handle encryption for sensitive data
      let encryptedData = undefined;
      if (request.data && (request.data.questions || request.data.notes || request.data.reflections || request.data.three_circles)) {
        try {
          const dataToEncrypt = {
            questions: request.data.questions,
            notes: request.data.notes,
            reflections: request.data.reflections,
            three_circles: request.data.three_circles,
          };
          
          encryptedData = await EncryptionService.encrypt(dataToEncrypt);
          
          // Data encrypted successfully
        } catch (encryptionError) {
          console.error('❌ Encryption failed, saving without encryption:', encryptionError);
          // Fall back to saving without encryption
          encryptedData = undefined;
        }
      } else {
        // No sensitive data to encrypt
      }
      
      // Prepare the request with encrypted data
      const requestWithEncryption = {
        ...request,
        data: {
          ...request.data,
          encryptedData: encryptedData,
          encrypted: !!encryptedData
        }
      };

      const response = await fetch(`${API_BASE_URL}/progress/step`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestWithEncryption)
      });

      await this.checkAuthError(response);
      if (!response.ok) {
        throw new Error(`Failed to update step progress: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating step progress:', error);
      throw error;
    }
  }

  async updateStepDates(stepNumber: number, dates: { started_at?: string; completed_at?: string }): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${API_BASE_URL}/progress/step/${stepNumber}/dates`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(dates)
      });

      await this.checkAuthError(response);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update step dates: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating step dates:', error);
      throw error;
    }
  }

  async updateActionProgress(request: { program: string; track: string; step_number: number; action_id: number; completed: boolean }): Promise<any> {
    return this.updateStepProgress({
      program: request.program,
      track: request.track,
      step_number: request.step_number,
      step_title: `Step ${request.step_number}`,
      completed: request.completed,
      data: { action_id: request.action_id, completed: request.completed }
    });
  }

  async updateStatus(request: { program: string; track: string; status_note: string; progress_milestone?: string }): Promise<any> {
    // Status updates are not used in the new structure
    return Promise.resolve({ message: 'Status update skipped' });
  }

  async getJourneyProgress(): Promise<JourneyProgress> {
    return perfTimeAsync('GET /progress/journey', async () => {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${API_BASE_URL}/progress/journey`, {
        method: 'GET',
        headers
      });

      await this.checkAuthError(response);
      if (!response.ok) {
        throw new Error(`Failed to get journey progress: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting journey progress:', error);
      throw error;
    }
    });
  }

  async updateDateTrackingPreference(optedOut: boolean): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${API_BASE_URL}/user/preferences/date-tracking`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ date_display_opted_out: optedOut })
      });

      await this.checkAuthError(response);
      if (!response.ok) {
        throw new Error(`Failed to update date tracking preference: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating date tracking preference:', error);
      throw error;
    }
  }

  async getProgressHistory(): Promise<{
    programs: Record<string, {
      tracks: Record<string, {
        steps: Record<number, {
          lastCompletedAt: string | null;
          totalCompletions: number;
        }>;
      }>;
    }>;
  }> {
    const allProgress = await this.getUserProgress();
    const history: {
      programs: Record<string, {
        tracks: Record<string, {
          steps: Record<number, {
            lastCompletedAt: string | null;
            totalCompletions: number;
          }>;
        }>;
      }>;
    } = {
      programs: {}
    };
    allProgress.forEach(item => {
      const { program, track, step_number, completed, completed_at } = item;
      if (!program || !track) return;
      if (!history.programs[program]) {
        history.programs[program] = { tracks: {} };
      }
      if (!history.programs[program].tracks[track]) {
        history.programs[program].tracks[track] = {
          steps: {}
        };
      }
      if (step_number && completed && completed_at) {
        if (!history.programs[program].tracks[track].steps[step_number]) {
          history.programs[program].tracks[track].steps[step_number] = {
            lastCompletedAt: null,
            totalCompletions: 0
          };
        }
        const stepData = history.programs[program].tracks[track].steps[step_number];
        stepData.totalCompletions++;
        if (!stepData.lastCompletedAt || new Date(completed_at) > new Date(stepData.lastCompletedAt)) {
          stepData.lastCompletedAt = completed_at;
        }
      }
    });
    return history;
  }

  private async migrateGettingStartedFromItems(
    items: ProgressItem[],
    program: string,
    track: string
  ): Promise<boolean> {
    try {
      const step0Record = this.findStepRecord(items, program, track, 0);
      if (step0Record?.completed) return false;

      const step1Record = this.findStepRecord(items, program, track, 1);
      if (!step1Record) return false;

      const step1 = this.buildStepOverviewItem(step1Record, await this.decryptRecordData(step1Record));
      const hasStep1Progress =
        step1.completed ||
        (Array.isArray(step1.data?.actions) &&
          step1.data.actions.some((a: { completed?: boolean }) => a.completed));

      if (!hasStep1Progress) return false;

      let existingData: Record<string, unknown> = {};
      if (step0Record) {
        existingData =
          this.buildStepOverviewItem(step0Record, await this.decryptRecordData(step0Record)).data ?? {};
      }

      const migratedActions = [1, 2, 3, 4, 5, 6, 7, 8].map((id) => ({ id, completed: true }));
      await this.updateStepProgress({
        program,
        track,
        step_number: 0,
        step_title: 'Getting Started',
        completed: true,
        data: {
          actions: migratedActions,
          migrated_from_step1: true,
          ...(existingData.three_circles ? { three_circles: existingData.three_circles } : {}),
          ...(existingData.sponsor_status ? { sponsor_status: existingData.sponsor_status } : {}),
        },
      });
      return true;
    } catch (error) {
      console.error('Error migrating Getting Started progress:', error);
      return false;
    }
  }

  /** Auto-complete Getting Started for users who already have Step 1 progress. */
  async migrateGettingStartedIfNeeded(): Promise<boolean> {
    return perfTimeAsync('migrateGettingStartedIfNeeded', async () => {
      const items = await this.getProgramProgress('SAA');
      return this.migrateGettingStartedFromItems(items, 'SAA', '12-steps');
    });
  }
}

export const progressService = new ProgressService(); 